import React, { useState } from "react";
import MenuItem from "./MenuItem";
import { MenuItem as MenuItemType } from "../MenuData";
import { ReactComponent as ChevronDownSvg } from "@assets/Icons/IconChevron-Down.svg";
import {
    CHEVRON_ICON,
    FOCUS_RING,
    ROW_STACK,
    SECTION_HEADER_PADDING,
    SECTION_HEADER_ROW,
    SECTION_HEADER_TEXT,
    TRANSITION_COLORS,
    TRANSITION_TRANSFORM,
} from "../sideMenuDensity";

interface MenuSectionProps {
    title: string;
    items: MenuItemType[];
    expandedSections: { [key: string]: boolean };
    setExpandedSections: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
    activeItem: string;
    setActiveItem: React.Dispatch<React.SetStateAction<string>>;
    onItemClick?: (item: MenuItemType) => void;
    isExpanded?: boolean;
}

const MenuSection: React.FC<MenuSectionProps> = ({
    title,
    items,
    expandedSections,
    setExpandedSections,
    activeItem,
    setActiveItem,
    onItemClick,
    isExpanded = true,
}) => {
    // Section-level collapse (open by default). Independent from `isExpanded`,
    // which controls whether the whole sidebar shows labels (icon-only mode).
    const [open, setOpen] = useState(true);

    // A collapsible group header is only shown when there's a title AND the
    // sidebar is showing labels. Title-less sections (e.g. Overview) render their
    // items directly, and in icon-only mode there are no headers/chevrons.
    const collapsible = !!title && isExpanded;

    return (
        <div>
            {collapsible && (
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-expanded={open}
                    /* `text-semantic-text-secondary`, not the literal `text-gray-500`
                       this carried: a hardcoded grey is the same ink in both themes,
                       so the header sat at the wrong contrast in dark while every
                       other muted string in the rail moved with the token. CTX-335
                       asks for the group label to read as muted in both. */
                    className={`${SECTION_HEADER_ROW} ${SECTION_HEADER_PADDING} text-semantic-text-secondary ${TRANSITION_COLORS} ${FOCUS_RING} hover:text-semantic-text-primary`}
                >
                    {/* Weight 500 (CTX-335, from 600): at 11px uppercase the heavier
                        cut competed with the row labels underneath it, which is the
                        opposite of what a group header should do. */}
                    <span className={`${SECTION_HEADER_TEXT} font-medium tracking-wide`}>{title}</span>
                    <ChevronDownSvg
                        className={`${CHEVRON_ICON} shrink-0 ${TRANSITION_TRANSFORM} ${open ? "" : "-rotate-90"}`}
                    />
                </button>
            )}
            {(open || !collapsible) && (
                <ul className={ROW_STACK}>
                    {items.map((item) => (
                        <MenuItem
                            key={item.id ?? item.label}
                            item={item}
                            expandedSections={expandedSections}
                            setExpandedSections={setExpandedSections}
                            activeItem={activeItem}
                            setActiveItem={setActiveItem}
                            onClick={(item) => (onItemClick ? onItemClick(item) : null)}
                            isExpanded={isExpanded}
                        />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default MenuSection;
